import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  PlayCircle, Loader2, MessageCircle, Bot, CalendarCheck, Send, BellRing, AlarmClock, CheckCircle2, RotateCcw,
} from "lucide-react";

const ICONS = { mensagem: MessageCircle, ia: Bot, agendamento: CalendarCheck, confirmacao: Send, notificacao: BellRing, lembrete: AlarmClock };

export function DemoRunner({ onDone }) {
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState([]);
  const [visible, setVisible] = useState(0);
  const [result, setResult] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const run = async () => {
    setRunning(true); setSteps([]); setVisible(0); setResult(null);
    try {
      const { data } = await api.post("/demo/run");
      setSteps(data.steps); setResult(data);
      data.steps.forEach((_, i) => setTimeout(() => setVisible(i + 1), 500 * (i + 1)));
      setTimeout(() => onDone?.(), 500 * data.steps.length + 100);
    } catch { toast.error("Não foi possível rodar a demonstração."); }
    finally { setRunning(false); }
  };

  useEffect(() => {
    if (location.state?.runDemo) { navigate(location.pathname, { replace: true, state: {} }); run(); }
  }, []); // eslint-disable-line

  const done = steps.length > 0 && visible >= steps.length;

  return (
    <div className="glow-card relative overflow-hidden rounded-2xl p-5" data-testid="demo-runner">
      <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-mono-flow uppercase tracking-widest text-cyan-400">Demo guiado</p>
          <h3 className="mt-1 font-display text-xl font-bold text-white">Veja a FlowIA trabalhando de ponta a ponta</h3>
          <p className="mt-1 max-w-xl text-sm text-slate-400">Simula um cliente real no WhatsApp: mensagem → resposta da IA → agendamento → confirmação → alerta → lembrete. Tudo em segundos.</p>
        </div>
        <Button onClick={run} disabled={running} data-testid="demo-run-btn" className="h-11 bg-cyan-400 px-5 font-semibold text-slate-950 hover:bg-cyan-300">
          {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> IA processando...</> : done ? <><RotateCcw className="mr-2 h-4 w-4" /> Rodar novamente</> : <><PlayCircle className="mr-2 h-4 w-4" /> Simular conversa de cliente</>}
        </Button>
      </div>

      {steps.length > 0 && (
        <ol className="relative mt-6 space-y-3 border-l border-slate-800 pl-6" data-testid="demo-steps">
          {steps.slice(0, visible).map((s, i) => {
            const Icon = ICONS[s.key] || CheckCircle2;
            const isIA = s.key === "ia";
            return (
              <li key={s.key} className="animate-flow-in relative" data-testid={`demo-step-${s.key}`}>
                <span className={`absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full ${isIA ? "bg-cyan-400 text-slate-950" : "bg-slate-800 text-cyan-300"}`}><Icon className="h-3 w-3" /></span>
                <p className="text-sm font-semibold text-slate-100"><span className="mr-2 font-mono-flow text-xs text-slate-500">0{i + 1}</span>{s.title}</p>
                <p className={`mt-1 whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${isIA ? "border border-cyan-400/30 bg-cyan-400/10 text-cyan-50" : "bg-slate-900/60 text-slate-300"}`}>{s.body}</p>
              </li>
            );
          })}
        </ol>
      )}

      {done && result && (
        <div className="mt-5 flex flex-wrap gap-2 animate-flow-in" data-testid="demo-done-actions">
          <Button size="sm" variant="outline" onClick={() => navigate("/inbox")} className="border-slate-700 bg-slate-900/50 text-slate-200" data-testid="demo-go-inbox">Ver no Inbox</Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/agenda")} className="border-slate-700 bg-slate-900/50 text-slate-200" data-testid="demo-go-agenda">Ver na Agenda</Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/clientes/${result.client_id}`)} className="border-slate-700 bg-slate-900/50 text-slate-200" data-testid="demo-go-client">Perfil do cliente</Button>
        </div>
      )}
    </div>
  );
}
